import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ClipboardList, Search, Truck, CheckCircle, ArrowRight, Phone, MessageSquare } from 'lucide-react';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1756644353075-7203fd4284d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxzcGFpbiUyMGNhZGl6JTIwY2l0eSUyMHN0cmVldHxlbnwwfHx8fDE3ODE2MTM1ODJ8MA&ixlib=rb-4.1.0&q=85';

const STEPS = [
  {
    icon: ClipboardList,
    step: '01',
    title: 'Crea tu solicitud',
    desc: 'Rellena el formulario con tus datos y los enseres que necesitas retirar.',
  },
  {
    icon: CheckCircle,
    step: '02',
    title: 'Confirmación inmediata',
    desc: 'Recibes un código de ticket por email o WhatsApp con la fecha asignada.',
  },
  {
    icon: Truck,
    step: '03',
    title: 'Recogida en domicilio',
    desc: 'Nuestro equipo pasa a recoger los enseres en el horario acordado.',
  },
  {
    icon: Search,
    step: '04',
    title: 'Seguimiento en tiempo real',
    desc: 'Consulta el estado de tu solicitud en cualquier momento con tu código.',
  },
];

const HomePage = () => {
  return (
    <div className="max-w-7xl mx-auto">
      {/* Hero Section */}
      <section
        className="relative rounded-2xl overflow-hidden mb-12 min-h-[440px] flex items-end"
        style={{ backgroundImage: `url(${HERO_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-slate-900/55" />
        <div className="relative z-10 p-10 md:p-14 max-w-2xl">
          <p className="text-teal-300 text-xs uppercase tracking-widest font-bold mb-3">Valoriza · Cádiz</p>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 tracking-tight">
            Solicitud de Recogida<br />de Enseres
          </h1>
          <p className="text-slate-200 text-base md:text-lg mb-8 leading-relaxed">
            Servicio municipal gratuito de recogida de muebles y enseres en tu domicilio. Fácil, rápido y con seguimiento en tiempo real.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/solicitud">
              <Button
                size="lg"
                className="bg-teal-500 hover:bg-teal-400 text-white px-8 py-4 text-base font-semibold"
                data-testid="btn-nueva-solicitud"
              >
                <ClipboardList className="w-5 h-5 mr-2" />
                Crear Solicitud
              </Button>
            </Link>
            <Link to="/consulta">
              <Button
                size="lg"
                variant="outline"
                className="border-white/50 text-white hover:bg-white/10 bg-transparent px-8 py-4 text-base"
                data-testid="btn-consultar-estado"
              >
                Consultar Estado
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Action Cards */}
      <section className="grid md:grid-cols-2 gap-6 mb-14">
        <div className="bg-white border border-slate-200 rounded-xl p-8 hover:border-teal-300 transition-all group" data-testid="card-nueva-solicitud">
          <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-5 group-hover:bg-teal-100 transition-colors">
            <ClipboardList className="w-6 h-6 text-teal-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Nueva Solicitud</h2>
          <p className="text-slate-500 text-sm leading-relaxed mb-6">
            Registra una nueva solicitud de recogida de enseres en 5 sencillos pasos. Sin burocracia, sin esperas.
          </p>
          <Link to="/solicitud">
            <Button className="bg-teal-600 hover:bg-teal-700 w-full" data-testid="btn-solicitud-card">
              Solicitar recogida
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-8 hover:border-blue-300 transition-all group" data-testid="card-consultar-estado">
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-5 group-hover:bg-blue-100 transition-colors">
            <Search className="w-6 h-6 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Consultar Estado</h2>
          <p className="text-slate-500 text-sm leading-relaxed mb-6">
            Introduce tu código de ticket y comprueba en tiempo real en qué estado se encuentra tu solicitud.
          </p>
          <Link to="/consulta">
            <Button variant="outline" className="border-blue-200 hover:border-blue-400 hover:text-blue-700 w-full" data-testid="btn-consulta-card">
              Ver mi solicitud
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* How it works */}
      <section className="mb-14" data-testid="how-it-works">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-widest font-bold text-teal-600 mb-1">El proceso</p>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Cómo funciona</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map(({ icon: Icon, step, title, desc }) => (
            <div key={step} className="bg-white border border-slate-200 rounded-xl p-6 relative">
              <span className="absolute top-4 right-5 text-slate-100 text-4xl font-black">{step}</span>
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-teal-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">{title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Channels */}
      <section className="bg-slate-900 rounded-2xl p-8 md:p-10">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-teal-400 text-xs uppercase tracking-widest font-bold mb-3">También puedes</p>
            <h2 className="text-2xl font-bold text-white mb-3 tracking-tight">Solicitar por WhatsApp o teléfono</h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              Además del portal web, puedes gestionar tu solicitud a través de WhatsApp o llamando a nuestro número de atención ciudadana.
            </p>
          </div>
          <div className="space-y-3">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-white font-medium">WhatsApp</p>
                <p className="text-slate-400 text-sm">Envía un mensaje y nuestro asistente te guiará</p>
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium">Teléfono</p>
                <p className="text-slate-400 text-sm">Atención telefónica de lunes a viernes</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
